/*
 * Bundler volume discount in the Flow cart drawer
 * ------------------------------------------------
 * Bundler (legacy / draft-order mode) only applies its discounts at checkout,
 * so Shopify's cart never contains them and the Flow drawer can't show them.
 *
 * This script asks Bundler itself what the discount is: it passes the current
 * cart to window.bndlr.updateCartWithDiscounts(), which runs Bundler's own
 * rules (tiers, products with the discount disabled, etc.), and then writes
 * the result into the drawer:
 *   - per line: crossed-out price + discounted price + discount badge
 *   - footer:  discount line + crossed-out subtotal + discounted subtotal
 *
 * Nothing is hardcoded: change or disable a discount in Bundler and the
 * drawer follows automatically.
 *
 * Install: assets/bundler-drawer-discount.js, loaded from layout/theme.liquid
 * (see the README that came with this file).
 */
(function () {
  'use strict';

  var DRAWER_SELECTOR = 'cart-drawer';
  var CARD_SELECTOR = '.cart-drawer__product_card';
  var FALLBACK_LABEL = 'Mengenrabatt';
  var INJECTED_ATTR = 'data-bndlr-injected';

  var scriptEl = document.currentScript;
  var moneyFormat =
    (scriptEl && scriptEl.getAttribute('data-money-format')) || '€{{amount_with_comma_separator}}';

  var observer = null;
  var timer = null;
  var running = false;
  var queued = false;

  // ---------- money formatting (Shopify money_format placeholders) ----------
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var number = (cents / 100).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? dollars + decimal + parts[1] : dollars;
  }

  function formatMoney(cents) {
    cents = Math.round(cents);
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount': return formatWithDelimiters(cents, 2, ',', '.');
        case 'amount_no_decimals': return formatWithDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return formatWithDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatWithDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatWithDelimiters(cents, 2, "'", '.');
        case 'amount_with_space_separator': return formatWithDelimiters(cents, 2, ' ', ',');
        default: return formatWithDelimiters(cents, 2, '.', ',');
      }
    });
  }

  // ---------- helpers ----------
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    node.setAttribute(INJECTED_ATTR, '');
    return node;
  }

  function removeInjected(root) {
    root.querySelectorAll('[' + INJECTED_ATTR + ']').forEach(function (n) { n.remove(); });
  }

  // Bundler's discount names, keyed by variant id (e.g. "Doppelpack")
  function labelsByVariant(result) {
    var labels = {};
    var combos = result.applicable_combos || [];
    combos.forEach(function (combo) {
      if (!combo || typeof combo !== 'object') return;
      Object.keys(combo).forEach(function (k) {
        var entry = combo[k];
        if (!entry || !entry.variant_id) return;
        var vd = entry.volume_discount;
        var label = (vd && vd.description) || entry.bundle_name || entry.name;
        if (label) labels[String(entry.variant_id)] = label;
      });
    });
    return labels;
  }

  function cardForItem(cards, item, index) {
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].getAttribute('data-bndlr-line-key') === item.key) return cards[i];
    }
    // Fallback: the drawer renders cart.items in order
    return cards[index] || null;
  }

  // ---------- render ----------
  function render(drawer, cart, result) {
    stopObserving();
    try {
      removeInjected(drawer);
      restoreOriginals(drawer);

      var discounted = result.discounted_cart_object;
      var discount = Number(result.discount_amount) || 0;
      if (!discounted || !discounted.items || discount <= 0) return;

      var labels = labelsByVariant(result);
      var discountedByKey = {};
      discounted.items.forEach(function (i) { discountedByKey[i.key] = i; });

      var cards = Array.prototype.slice.call(drawer.querySelectorAll(CARD_SELECTOR));
      var usedLabels = [];

      cart.items.forEach(function (item, index) {
        var d = discountedByKey[item.key];
        if (!d) return;
        var newLine = Number(d.line_price);
        var oldLine = Number(item.final_line_price);
        if (!(newLine < oldLine)) return;

        var label = labels[String(item.variant_id)] || FALLBACK_LABEL;
        if (usedLabels.indexOf(label) === -1) usedLabels.push(label);

        var card = cardForItem(cards, item, index);
        if (!card) return;

        // Price: crossed-out current unit price, discounted unit price
        var priceBox = card.querySelector('.cart-drawer__product-card--price');
        if (priceBox) {
          var spans = priceBox.querySelectorAll('span:not(.visually-hidden)');
          var current = spans[spans.length - 1];
          if (current) {
            current.setAttribute('data-bndlr-original-class', current.className);
            current.setAttribute('data-bndlr-original-text', current.innerHTML);
            if (!priceBox.querySelector('.cart-drawer__product-card--price--compare')) {
              var compare = el('span', 'cart-drawer__product-card--price--compare', formatMoney(item.final_price));
              compare.style.marginRight = '0.35em';
              priceBox.insertBefore(compare, current);
            }
            current.className = 'cart-drawer__product-card--price--sale';
            current.textContent = formatMoney(newLine / item.quantity);
          }
        }

        // Badge under the title, same style as Shopify discounts in Flow
        var body = card.querySelector('.cart-drawer__product-card--info--body');
        if (body) {
          var badge = el('div', 'cart-drawer__product_card--badge cart-drawer__product_card--badge--sale');
          badge.appendChild(el('span', null, label + ' (-' + formatMoney(oldLine - newLine) + ')'));
          var details = body.querySelector('.cart-drawer__product-card--details');
          body.insertBefore(badge, details || null);
        }
      });

      // Footer: discount line + subtotal
      var subtotal = drawer.querySelector('.cart-drawer__subtotal');
      if (subtotal) {
        var wrap = el('div', 'cart-drawer__discounts');
        var badge = el('div', 'cart-drawer__product_card--badge cart-drawer__product_card--badge--sale');
        badge.appendChild(el('span', null, (usedLabels.join(', ') || FALLBACK_LABEL) + ' (-' + formatMoney(discount) + ')'));
        wrap.appendChild(badge);
        subtotal.parentNode.insertBefore(wrap, subtotal);

        var priceWrap = subtotal.querySelector('.cart-drawer__subtotal--price');
        if (priceWrap) {
          var totalSpans = priceWrap.querySelectorAll('span:not(.visually-hidden)');
          var totalEl = totalSpans[totalSpans.length - 1];
          if (totalEl) {
            totalEl.setAttribute('data-bndlr-original-class', totalEl.className);
            totalEl.setAttribute('data-bndlr-original-text', totalEl.innerHTML);
            if (!priceWrap.querySelector('.cart-drawer__subtotal--compare')) {
              var cmp = el('span', 'cart-drawer__subtotal--compare', formatMoney(cart.total_price));
              cmp.style.marginRight = '0.35em';
              priceWrap.insertBefore(cmp, totalEl);
            }
            totalEl.className = 'cart-drawer__subtotal--sale';
            totalEl.textContent = formatMoney(result.total_amount_discounted);
          }
        }
      }
    } finally {
      startObserving(drawer);
    }
  }

  // Undo our edits to theme-rendered spans before re-rendering
  function restoreOriginals(drawer) {
    drawer.querySelectorAll('[data-bndlr-original-text]').forEach(function (n) {
      n.innerHTML = n.getAttribute('data-bndlr-original-text');
      n.className = n.getAttribute('data-bndlr-original-class') || '';
      n.removeAttribute('data-bndlr-original-text');
      n.removeAttribute('data-bndlr-original-class');
    });
  }

  // ---------- main ----------
  function update() {
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    if (!window.bndlr || typeof window.bndlr.updateCartWithDiscounts !== 'function') {
      // Bundler not loaded yet: try again shortly
      clearTimeout(timer);
      timer = setTimeout(update, 500);
      return;
    }
    if (running) { queued = true; return; }
    running = true;

    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var result = window.bndlr.updateCartWithDiscounts(JSON.parse(JSON.stringify(cart)));
        render(drawer, cart, result || {});
      })
      .catch(function (e) { console.warn('[bundler-drawer-discount]', e); })
      .then(function () {
        running = false;
        if (queued) { queued = false; schedule(); }
      });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(update, 200);
  }

  function isOwnMutation(m) {
    var nodes = Array.prototype.slice.call(m.addedNodes).concat(Array.prototype.slice.call(m.removedNodes));
    return nodes.length > 0 && nodes.every(function (n) {
      return n.nodeType !== 1 ? true : n.hasAttribute(INJECTED_ATTR);
    });
  }

  function startObserving(drawer) {
    if (!observer) {
      observer = new MutationObserver(function (mutations) {
        if (mutations.some(function (m) { return !isOwnMutation(m); })) schedule();
      });
    }
    observer.observe(drawer, { childList: true, subtree: true });
  }

  function stopObserving() {
    if (observer) observer.disconnect();
  }

  function init() {
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    startObserving(drawer);
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.bundlerDrawerDiscount = { update: update };
})();